import { useContext, useEffect, useRef, useState } from 'react'
import { Accordion, Button, TextField, Checkbox, FileUpload, UploadedFile, Flex, Box, Text } from 'src/componentv2'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'

interface Props {
    open: boolean
    onToggle: (o: boolean) => void
    onSaveAndNext?: () => void
}

const NUMERIC_FIELDS = ['twoFa', 'defaultOneTime', 'defaultDaily', 'maxOneTime', 'maxDaily'] as const
type NumericField = typeof NUMERIC_FIELDS[number]

const FIELD_TO_FORM_KEY: Record<NumericField, string> = {
    twoFa: 'TwoFactorAuthenticationThresholdAmount',
    defaultOneTime: 'DefaultOneTimeTransactionLimit',
    defaultDaily: 'DefaultDailyTransactionLimit',
    maxOneTime: 'MaximumOneTimeTransactionLimit',
    maxDaily: 'MaximumDailyTransactionLimit',
}

const stripNonDigits = (v: string) => v.replace(/[^0-9]/g, '')

export const TransactionSettings: React.FC<Props> = ({ open, onToggle, onSaveAndNext }) => {
    const { getInstitutionProfile, updateTransactionPolicy } = useContext(ConfigurationApiContext)

    const [availableBalance, setAvailableBalance] = useState(false)
    const [withdrawableBalance, setWithdrawableBalance] = useState(false)
    const [thresholds, setThresholds] = useState<Record<NumericField, string>>({
        twoFa: '', defaultOneTime: '', defaultDaily: '', maxOneTime: '', maxDaily: '',
    })
    const [stampFile, setStampFile] = useState<UploadedFile | null>(null)
    const stampFileRaw = useRef<File | null>(null) // actual File for upload; UploadedFile is just for display
    const [saving, setSaving] = useState(false)

    /* InstitutionProfileDetails has no threshold/limit/toggle fields — the only
       thing worth prefilling here is the existing stamp/signature, via `stamp`. */
    useEffect(() => {
        (async () => {
            const profile = await getInstitutionProfile()
            if (profile?.stamp) {
                setStampFile({ name: 'Current signature/stamp', size: '' })
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const set = (key: NumericField) => (v: string) =>
        setThresholds((t) => ({ ...t, [key]: stripNonDigits(v) }))

    const handleFileSelect = (f: File) => {
        stampFileRaw.current = f
        setStampFile({ name: f.name, size: `${Math.round(f.size / 1024)} KB` })
    }

    const handleFileRemove = () => {
        stampFileRaw.current = null
        setStampFile(null)
    }

    const handleSave = async () => {
        setSaving(true)

        const payload: Record<string, any> = {
            ShowAvailableBalance: availableBalance,
            ShowWithdrawalBalance: withdrawableBalance,
        }
        NUMERIC_FIELDS.forEach((key) => {
            const raw = thresholds[key]
            if (raw !== '') payload[FIELD_TO_FORM_KEY[key]] = Number(raw)
        })
        // only send the file if the user picked a new one this session — otherwise
        // leave it out so we don't overwrite the existing stamp with nothing
        if (stampFileRaw.current) {
            payload.StatementStampAndSignature = stampFileRaw.current
        }

        const result = await updateTransactionPolicy(payload)

        setSaving(false)
        if (result !== false && result !== null) {
            onSaveAndNext?.()
        }
    }

    return (
        <Accordion title="Transaction Settings" open={open} onToggle={onToggle}>
            <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                Show Available balance and withdrawable balance
            </Text>
            <Flex gap="3.2" items="center">
                <Checkbox
                    className="checkbox--single-line"
                    checked={availableBalance}
                    onChange={setAvailableBalance}
                    label="Available balance"
                />
                <Checkbox
                    className="checkbox--single-line"
                    checked={withdrawableBalance}
                    onChange={setWithdrawableBalance}
                    label="Withdrawable balance"
                />
            </Flex>

            {[
                ['twoFa', '2FA Transaction Threshold Amount'],
                ['defaultOneTime', 'Default One Time Transaction Limit'],
                ['defaultDaily', 'Default Daily Transaction Limit'],
                ['maxOneTime', 'Maximum One Time Transaction Limit'],
                ['maxDaily', 'Maximum Daily Transaction Limit'],
            ].map(([key, label]) => (
                <Box topMargin="1.6" key={key}>
                    <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                        {label}
                    </Text>
                    <TextField
                        placeholder="XXXXXXXXX"
                        inputProps={{ inputMode: 'numeric' }}
                        value={thresholds[key as NumericField]}
                        onChange={set(key as NumericField)}
                    />
                </Box>
            ))}

            <Box topMargin="1.6">
                <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                    Statement stamp and signature
                </Text>
                <FileUpload
                    value={stampFile}
                    hint="JPG or PNG Preferred"
                    label="Click to upload image of statement stamp and signature"
                    accept=".jpg,.jpeg,.png"
                    onSelect={handleFileSelect}
                    onRemove={handleFileRemove}
                />
            </Box>

            <Flex justify="flex-end" topMargin="2.4" className="accordion__footer">
                <Button variant="ghost" size="sm" disabled={saving} onClick={handleSave}>
                    {saving ? 'Saving…' : 'Save Changes'}
                </Button>
            </Flex>
        </Accordion>
    )
}

export default TransactionSettings